import React from "react";
import { Box, Typography, Stack } from "@mui/joy";

const steps = [
  {
    title: "Bill Introduction",
    description:
      "A member of Congress writes and introduces a bill — an idea for a new law. It can originate in either the House or Senate.",
    points: [
      "The bill receives a number and a short title.",
      "It is assigned to a committee whose members specialize in that topic.",
    ],
  },
  {
    title: "Committee Review",
    description:
      "The committee examines the bill in detail, often holding hearings where experts and the public can testify.",
    points: [
      "Members may amend the bill to strengthen or narrow it.",
      "The committee votes to advance it to the full chamber, table it indefinitely, or reject it outright.",
    ],
  },
  {
    title: "Floor Debate & Vote",
    description:
      "If the committee approves, the full House or Senate debates the bill and proposes further amendments.",
    points: [
      "Lawmakers vote to pass or reject it.",
      "If it passes, it moves to the other chamber and repeats the same process.",
    ],
  },
  {
    title: "Presidential Action",
    description:
      "Once both chambers pass identical versions, the bill goes to the President for final action.",
    points: [
      "The President can sign it into law or veto it.",
      "Congress can override a veto with a two-thirds vote in both chambers.",
      "If the President takes no action within 10 days while Congress is in session, the bill becomes law automatically.",
    ],
  },
];

const LearnTheProcess = () => {
  return (
    <Box
      sx={{
        maxWidth: "1200px",
        mx: "auto",
        px: { xs: "1.25rem", md: "2rem" },
        py: { xs: "3.5rem", md: "5rem" },
      }}
    >
      {/* Section header */}
      <Box sx={{ mb: { xs: "2.5rem", md: "3.5rem" } }}>
        <Typography
          component="p"
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            color: "#0075b2",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            mb: "10px",
          }}
        >
          Step by step
        </Typography>
        <Typography
          level="h2"
          sx={{
            fontSize: { xs: "28px", md: "36px" },
            fontWeight: 700,
            color: "#171a1c",
            lineHeight: 1.2,
            maxWidth: "480px",
          }}
        >
          The Legislative Process
        </Typography>
      </Box>

      {/* Steps grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        {steps.map((step, index) => {
          const isLeft = index % 2 === 0;
          const isBottom = index >= 2;
          return (
            <Box
              key={index}
              sx={{
                padding: { xs: "2rem 0", md: "2.5rem" },
                borderTop: "1px solid rgba(0,0,0,0.07)",
                borderRight: {
                  md: isLeft ? "1px solid rgba(0,0,0,0.07)" : "none",
                },
                paddingLeft: { md: isLeft ? 0 : "2.5rem" },
                paddingRight: { md: isLeft ? "2.5rem" : 0 },
              }}
            >
              <Typography
                component="p"
                sx={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#0075b2",
                  letterSpacing: "0.04em",
                  mb: "12px",
                }}
              >
                0{index + 1}
              </Typography>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "#171a1c",
                  mb: "10px",
                  lineHeight: 1.3,
                }}
              >
                {step.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: "15px",
                  color: "#535862",
                  lineHeight: 1.65,
                  mb: "14px",
                }}
              >
                {step.description}
              </Typography>
              <Box
                component="ul"
                sx={{
                  margin: 0,
                  paddingLeft: "18px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                {step.points.map((point, i) => (
                  <Box
                    key={i}
                    component="li"
                    sx={{ fontSize: "14px", color: "#6b7280", lineHeight: 1.6 }}
                  >
                    {point}
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default LearnTheProcess;
